import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Eye, EyeOff, ArrowRight } from 'lucide-react';

const fadeUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5 } }
};
const stagger = { visible: { transition: { staggerChildren: 0.08 } } };

export default function SignIn() {
  const [form, setForm] = useState({ email: '', password: '' });
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!form.email || !form.password) {
      setError('Please fill in all fields');
      return;
    }
    setLoading(true);
    const result = await login(form.email, form.password);
    if (result.success) {
      navigate('/app/dashboard');
    } else {
      setError(result.error);
    }
    setLoading(false);
  };

  const inputClass = "bg-[#111122] border-white/10 text-white placeholder:text-slate-600 focus:border-[#00d4ff] focus:ring-[#00d4ff]/20 h-11";

  return (
    <div className="min-h-screen bg-[#0a0a1a] flex items-center justify-center py-12 px-6 relative">
      <div className="glow-spot w-[400px] h-[400px] bg-[#3b82f6] top-[-100px] right-[-100px] opacity-10" />
      <div className="glow-spot w-[300px] h-[300px] bg-[#00d4ff] bottom-[-100px] left-[-100px] opacity-10" style={{ animationDelay: '2s' }} />

      <motion.div initial="hidden" animate="visible" variants={stagger} className="w-full max-w-md relative">
        <motion.div variants={fadeUp} className="text-center mb-8">
          <Link to="/" className="text-2xl font-black text-white tracking-tight inline-block" style={{ fontFamily: 'Outfit' }}>
            <span className="text-[#00d4ff]">D</span>QP
          </Link>
          <h1 className="text-2xl font-bold text-white mt-4" style={{ fontFamily: 'Outfit' }}>Welcome back</h1>
          <p className="text-slate-400 text-sm mt-2">Sign in to your DQP account</p>
        </motion.div>

        <motion.form variants={fadeUp} data-testid="signin-form" onSubmit={handleSubmit} className="glass-card p-8 space-y-5">
          {error && (
            <div data-testid="signin-error" className="bg-red-500/10 border border-red-500/20 rounded-lg p-3 text-red-400 text-sm">{error}</div>
          )}
          <div>
            <Label className="text-slate-300 text-sm mb-2 block">Email</Label>
            <Input data-testid="signin-email-input" type="email" placeholder="you@company.com" value={form.email} onChange={e => setForm({...form, email: e.target.value})} className={inputClass} />
          </div>
          <div>
            <Label className="text-slate-300 text-sm mb-2 block">Password</Label>
            <div className="relative">
              <Input data-testid="signin-password-input" type={showPw ? 'text' : 'password'} placeholder="Your password" value={form.password} onChange={e => setForm({...form, password: e.target.value})} className={`${inputClass} pr-10`} />
              <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300">
                {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
          </div>
          <div className="flex items-center justify-end">
            <span className="text-[#00d4ff] text-sm cursor-pointer hover:underline">Forgot password?</span>
          </div>
          <Button data-testid="signin-submit-btn" type="submit" disabled={loading} className="w-full bg-gradient-to-r from-[#00d4ff] to-[#3b82f6] text-[#0a0a1a] font-semibold rounded-full h-11 hover:opacity-90 shadow-[0_0_20px_rgba(0,212,255,0.3)] disabled:opacity-50">
            {loading ? 'Signing in...' : <>Sign In <ArrowRight className="ml-2 w-4 h-4" /></>}
          </Button>
        </motion.form>

        <motion.p variants={fadeUp} className="text-center mt-6 text-slate-500 text-sm">
          Don't have an account? <Link to="/signup" data-testid="signin-signup-link" className="text-[#00d4ff] hover:underline">Sign Up</Link>
        </motion.p>
      </motion.div>
    </div>
  );
}
